import { useEffect } from 'react';
import type { CaptchaAction } from '@aikka/util';

/**
 * reCAPTCHA v3 côté navigateur : invisible, sans case à cocher. Le script de
 * Google rend un jeton par action, que l'API fait vérifier (`RecaptchaGuard`).
 *
 * CHARGÉ SEULEMENT SUR LES ÉCRANS QUI L'EXIGENT — inscription, mot de passe
 * oublié, réinitialisation — et pas dans `index.html` : un script tiers qui
 * dépose ses cookies n'a rien à faire sur la landing ni dans le produit.
 *
 * INERTE SANS CLÉ. Tant que `VITE_RECAPTCHA_SITE_KEY` n'est pas posé dans
 * Amplify, rien ne se charge et aucun jeton ne part — l'API, sans sa clé
 * secrète, n'en demande pas. Les deux clés s'activent ensemble.
 */
const SITE_KEY: string | undefined = import.meta.env.VITE_RECAPTCHA_SITE_KEY || undefined;

/** Au-delà, on soumet sans jeton : l'API refusera proprement si elle en exige un. */
const TOKEN_TIMEOUT_MS = 5_000;

interface Grecaptcha {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let loader: Promise<Grecaptcha> | null = null;

export function isRecaptchaEnabled(): boolean {
  return Boolean(SITE_KEY);
}

/**
 * Le badge flottant de reCAPTCHA recouvrait le bandeau de consentement, en bas
 * à droite. Google autorise à le masquer À CONDITION d'afficher sa mention sous
 * le formulaire : c'est `AuthRecaptchaNotice`. La règle est posée ici, avec le
 * script, plutôt que dans `globals.css` — elle n'a de sens que là où le script
 * se charge, et une feuille globale change l'empreinte SEO de chaque page.
 */
function hideBadge(): void {
  if (document.getElementById('aikka-recaptcha-badge')) return;
  const style = document.createElement('style');
  style.id = 'aikka-recaptcha-badge';
  style.textContent = '.grecaptcha-badge { visibility: hidden; }';
  document.head.appendChild(style);
}

function loadRecaptcha(siteKey: string): Promise<Grecaptcha> {
  loader ??= new Promise<Grecaptcha>((resolve, reject) => {
    hideBadge();
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => {
      const g = window.grecaptcha;
      if (g) g.ready(() => resolve(g));
      else reject(new Error('grecaptcha missing after load'));
    };
    script.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    // Un bloqueur, un réseau coupé : on oubliera l'échec pour réessayer au prochain envoi.
    loader = null;
    throw err;
  });
  return loader;
}

/** Charge le script dès l'arrivée sur l'écran, pour que le jeton soit prêt à l'envoi. */
export function usePreloadRecaptcha(): void {
  useEffect(() => {
    if (SITE_KEY) loadRecaptcha(SITE_KEY).catch(() => undefined);
  }, []);
}

/**
 * Un jeton frais pour `action` (ils expirent en deux minutes : on en demande un
 * à chaque envoi, jamais à l'avance). `undefined` quand reCAPTCHA est inactif
 * ou n'a pas pu se charger — le formulaire part quand même, et c'est l'API qui
 * décide.
 */
export async function getCaptchaToken(action: CaptchaAction): Promise<string | undefined> {
  if (!SITE_KEY) return undefined;
  try {
    const grecaptcha = await loadRecaptcha(SITE_KEY);
    return await Promise.race([
      grecaptcha.execute(SITE_KEY, { action }),
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), TOKEN_TIMEOUT_MS)),
    ]);
  } catch {
    return undefined;
  }
}
